import {
  createSupplierAutoPersistenceSnapshotFromSosSuppliers,
  createSupplierAutoSosAdapterPayloadFromSuppliers,
  type SupplierAutoMaterial,
  type SupplierAutoPersistenceAdapter,
  type SupplierAutoPersistenceSnapshot,
  type SupplierAutoSosSupplierRecord,
} from '@dvvb/supplier-auto-core';

export const SUPPLIER_AUTO_TOKEN_KEY = 'dvvb.auth.token';

interface ApiEnvelope<T> {
  data?: T;
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
}

interface ListEnvelope<T> {
  items: T[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

interface LoginResponse {
  userSessionToken: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
  organizations: Array<{
    id: string;
    name: string;
    role: string;
  }>;
}

interface InventoryMaterialRecord {
  id: string;
  locationName?: string | null;
  name: string;
  unit?: string | null;
  minStock?: number | null;
  targetStock?: number | null;
  stock?: number | null;
  providerKind?: string | null;
  providerUnit?: string | null;
  comparableQuantity?: number | null;
  comparableUnit?: string | null;
  orderEquivalentQuantity?: number | null;
  updatedAt?: string | null;
  createdAt?: string | null;
}

export interface SosSupplierAutoAdapterOptions {
  baseUrl: string;
  getToken: () => string | null;
}

export function createSosSupplierAutoPersistenceAdapter({
  baseUrl,
  getToken,
}: SosSupplierAutoAdapterOptions): SupplierAutoPersistenceAdapter {
  let existingSuppliers: SupplierAutoSosSupplierRecord[] = [];
  let loadedMaterials: SupplierAutoMaterial[] = [];

  return {
    async load() {
      const [suppliers, materials] = await Promise.all([
        listAllSuppliers(baseUrl, getToken),
        listAllMaterials(baseUrl, getToken),
      ]);
      existingSuppliers = suppliers;
      loadedMaterials = materials;
      return createSupplierAutoPersistenceSnapshotFromSosSuppliers(
        suppliers,
        materials,
        new Date().toISOString(),
      );
    },

    async save(snapshot: SupplierAutoPersistenceSnapshot) {
      const payload = createSupplierAutoSosAdapterPayloadFromSuppliers(snapshot.suppliers, {
        existingSuppliers,
        savedAt: new Date().toISOString(),
      });

      if (!payload.report.ready) {
        throw new Error(`저장 차단 ${payload.report.blockingIssueCount}건`);
      }

      for (const upsert of payload.upserts) {
        if (upsert.operation === 'update' && upsert.sosSupplierId) {
          await apiRequest<{ item: SupplierAutoSosSupplierRecord }>(
            baseUrl,
            `/v1/suppliers/${upsert.sosSupplierId}`,
            getToken,
            {
              method: 'PATCH',
              body: JSON.stringify(upsert.input),
            },
          );
        } else {
          await apiRequest<{ item: SupplierAutoSosSupplierRecord }>(
            baseUrl,
            '/v1/suppliers',
            getToken,
            {
              method: 'POST',
              body: JSON.stringify(upsert.input),
            },
          );
        }
      }

      existingSuppliers = await listAllSuppliers(baseUrl, getToken);
      return createSupplierAutoPersistenceSnapshotFromSosSuppliers(
        existingSuppliers,
        snapshot.materials ?? loadedMaterials,
        new Date().toISOString(),
      );
    },
  };
}

export async function loginToSosApi(baseUrl: string, email: string, password: string): Promise<LoginResponse> {
  return apiRequest<LoginResponse>(baseUrl, '/v1/auth/login', () => null, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function readStoredSupplierAutoToken(): string | null {
  try {
    return localStorage.getItem(SUPPLIER_AUTO_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeStoredSupplierAutoToken(token: string | null) {
  try {
    if (token) {
      localStorage.setItem(SUPPLIER_AUTO_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(SUPPLIER_AUTO_TOKEN_KEY);
    }
  } catch {
    /* noop */
  }
}

async function listAllSuppliers(
  baseUrl: string,
  getToken: () => string | null,
): Promise<SupplierAutoSosSupplierRecord[]> {
  return listAllPages<SupplierAutoSosSupplierRecord>(baseUrl, '/v1/suppliers', getToken);
}

async function listAllMaterials(
  baseUrl: string,
  getToken: () => string | null,
): Promise<SupplierAutoMaterial[]> {
  const rows = await listAllPages<InventoryMaterialRecord>(baseUrl, '/v1/inventory/materials', getToken);
  return rows.map(mapInventoryMaterialRecord);
}

async function listAllPages<T>(
  baseUrl: string,
  path: string,
  getToken: () => string | null,
): Promise<T[]> {
  const items: T[] = [];
  let cursor = '';

  for (let page = 0; page < 100; page += 1) {
    const params = new URLSearchParams({ limit: '500' });
    if (cursor) {
      params.set('cursor', cursor);
    }

    const envelope = await apiRequest<ListEnvelope<T>>(
      baseUrl,
      `${path}?${params.toString()}`,
      getToken,
      { method: 'GET' },
    );
    items.push(...(Array.isArray(envelope.items) ? envelope.items : []));
    if (!envelope.has_more || !envelope.cursor) {
      break;
    }
    cursor = envelope.cursor;
  }

  return items;
}

async function apiRequest<T>(
  baseUrl: string,
  path: string,
  getToken: () => string | null,
  init: RequestInit,
): Promise<T> {
  const token = getToken();
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  const json = await response.json().catch(() => ({})) as ApiEnvelope<T> | T;
  const envelope = isRecord(json) && ('data' in json || 'error' in json) ? json as ApiEnvelope<T> : null;
  if (!response.ok || envelope?.error) {
    throw new Error(envelope?.error?.message ?? `API 오류 (${response.status})`);
  }

  return envelope ? envelope.data as T : json as T;
}

function mapInventoryMaterialRecord(row: InventoryMaterialRecord): SupplierAutoMaterial {
  const comparisonUnit = row.comparableUnit || row.providerUnit || row.unit || 'ea';
  const currentStock = toNumber(row.comparableQuantity, toNumber(row.orderEquivalentQuantity, toNumber(row.stock, 0)));
  return {
    id: row.id,
    name: row.name,
    category: row.providerKind === 'boxhero' ? 'BoxHero 연동' : 'DVVB 재료',
    locationName: row.locationName || '사업장 미지정',
    currentStock,
    minStock: toNumber(row.minStock, 0),
    targetStock: toNumber(row.targetStock, 0),
    comparisonUnit,
    orderUnit: row.providerUnit || comparisonUnit,
    lastInboundAt: formatDate(row.updatedAt || row.createdAt),
  };
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  return value.slice(0, 10);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
